import { digitFixer } from "utils/digitfixer.util";

/** Admin-facing MC aggregator labels. */
const AGGREGATOR_DISPLAY_NAMES: Record<string, string> = {
  mc: "MC",
};

const AGGREGATOR_SOURCE_DISPLAY: Record<string, string> = {
  mc: "MC",
  talixo: "Talixo",
};

export const formatAggregatorSource = (source?: string): string => {
  if (!source) return "—";
  const key = String(source).toLowerCase();
  if (AGGREGATOR_SOURCE_DISPLAY[key]) return AGGREGATOR_SOURCE_DISPLAY[key];
  if (key !== "talixo" && key !== "darbi") return "MC";
  return key.charAt(0).toUpperCase() + key.slice(1);
};

export const getAggregatorName = (booking: any): string => {
  const name =
    booking?.aggregator_booking?.provider ||
    booking?.aggregator_details?.aggregator_name ||
    "";
  if (!name) return "External";
  const key = String(name).toLowerCase();
  if (key === "mc") return "MC";
  if (key === "darbi") return "Darbi";
  if (AGGREGATOR_DISPLAY_NAMES[key]) return AGGREGATOR_DISPLAY_NAMES[key];
  if (key !== "talixo") return "MC";
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const isMcAggregatorTrip = (booking: any): boolean => {
  const provider =
    booking?.aggregator_booking?.provider ||
    booking?.aggregator_details?.aggregator_name ||
    "";
  return String(provider).toLowerCase() === "mc";
};

export const getListingPartnerLabel = (booking: any): string | null => {
  if (booking?.partner_details?.company_name) return null;
  if (isMcAggregatorTrip(booking)) return "MC partner";
  return null;
};

export const isAggregatorTrip = (booking: any): boolean =>
  !!(
    booking?.isAggregator ||
    booking?.aggregator_booking_id ||
    booking?.aggregator_booking
  );

/** External trip id when booking is synced to MC. */
export const getAggregatorExternalTripId = (booking: any): string =>
  String(
    booking?.aggregator_booking?.external_trip_id ||
      booking?.aggregator_details?.trip_id ||
      ""
  ).trim();

/** MC / external provider booking page URL from GET /booking enrichment. */
export const getMcProviderBookingUrl = (booking: any): string =>
  String(booking?.aggregator_booking?.external_booking_url || "").trim();

/**
 * Active synced aggregator bookings: cancel does not auto-refund — use Refund.
 */
export const shouldHideStandaloneRefund = (_booking: any): boolean => false;

/** Hide MC sync badge when Yelo status already reflects cancellation. */
export const shouldShowAggregatorSyncStatus = (booking: any): boolean => {
  if (!isAggregatorTrip(booking)) return false;
  const syncStatus = booking?.aggregator_booking?.sync_status;
  if (!syncStatus || syncStatus === "synced" || syncStatus === "cancelled") {
    return false;
  }
  if (booking?.current_status === "cancelled" || booking?.is_cancelled) {
    return false;
  }
  return true;
};

export const formatAggregatorMarkupName = (aggregator?: string): string => {
  if (!aggregator) return "Aggregator";
  const key = String(aggregator).toLowerCase();
  if (key === "mc") return "MC";
  return aggregator.charAt(0).toUpperCase() + aggregator.slice(1);
};

export const isBookingPaid = (booking: any): boolean =>
  String(booking?.payment_status || "").trim().toLowerCase() === "paid";

/** MC trip saved in Yelo but not yet created on the provider API. */
export const isPendingMcSync = (booking: any): boolean => {
  if (!isAggregatorTrip(booking)) return false;
  if (getAggregatorExternalTripId(booking)) return false;
  const status = booking?.aggregator_booking?.sync_status;
  return !status || ["quoted", "pending_payment", "failed"].includes(status);
};

/** Admin MC trip ready to push to provider (paid or unpaid). */
export const canSendToMc = (booking: any): boolean => {
  if (!isPendingMcSync(booking)) return false;
  if (booking?.current_status === "cancelled" || booking?.is_cancelled) {
    return false;
  }
  return true;
};

/** MC trip already created on the external provider API. */
export const isSyncedMcBooking = (booking: any): boolean =>
  isAggregatorTrip(booking) && !!getAggregatorExternalTripId(booking);

/** Show MC provider card on booking details (quote, prices, send action). */
export const canShowMcProviderCard = (booking: any): boolean => {
  if (!isAggregatorTrip(booking)) return false;
  if (booking?.aggregator_booking) return true;
  return !!(
    booking?.aggregator_booking_id ||
    booking?.aggregator_details?.class_id ||
    booking?.aggregator_details?.aggregator_name
  );
};

export type McAggregatorPricing = {
  mcPrice: string | null;
  yeloPriceAfterMarkup: string | null;
  markupPrice: string | null;
  markupPriceLabel: string;
};

const formatMcPrice = (value: number | null | undefined, currency?: string) => {
  if (value == null || !currency) return null;
  return `${digitFixer(value)} ${currency}`;
};

const resolveMcMarkupPercent = (
  agg: any,
  mcPrice: number | null,
  yeloPrice: number | null
): number | null => {
  // Use the frozen price pair first — this reflects the markup applied at quote time
  // and does not change when Dynamics markup is edited later.
  if (mcPrice != null && yeloPrice != null && mcPrice > 0) {
    return digitFixer(((yeloPrice / mcPrice) - 1) * 100);
  }

  const stored = Number(agg?.markup_percent);
  if (Number.isFinite(stored) && stored >= 0) return digitFixer(stored);

  return null;
};

/** MC base price, Yelo marked-up price, markup amount, and markup % for booking details. */
export const getMcAggregatorPricing = (agg: any): McAggregatorPricing => {
  const currency = agg?.currency;
  const yeloPrice =
    agg?.quoted_price != null ? Number(agg.quoted_price) : null;
  const mcPrice =
    agg?.supplier_quoted_price != null
      ? Number(agg.supplier_quoted_price)
      : null;

  const markupAmount =
    yeloPrice != null && mcPrice != null
      ? digitFixer(yeloPrice - mcPrice)
      : null;
  const markupPercent = resolveMcMarkupPercent(agg, mcPrice, yeloPrice);
  const markupPercentLabel =
    markupPercent != null ? `${digitFixer(markupPercent)}%` : null;

  return {
    mcPrice: formatMcPrice(mcPrice, currency),
    yeloPriceAfterMarkup: formatMcPrice(yeloPrice, currency),
    markupPrice: formatMcPrice(markupAmount, currency),
    markupPriceLabel: markupPercentLabel
      ? `Margin (${markupPercentLabel})`
      : "Margin",
  };
};
